import cx from 'classnames';

import { Box, Button } from '@zillow/constellation-10';
import { LoadingButton } from '@zg-rentals/loading-components';

import { useSelector } from '../../../../selectors/useSelector';

import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';

import LeaseCard from '../LeaseCard';

import { ConnectedLeaseContainer, StyledFlexLeaseCard } from './StyledComponents';
import ManageTenantsCSEvents from '../../../../model/ClickstreamEvent/ManageTenantsCSEvents';

import type { LeasesSummaryResponse } from '../../../../hooks/queries/summary/types';

const CREATE_A_LEASE = 'Create a lease';

type Props = {
  leaseCards: LeasesSummaryResponse['leaseCards'];
  ctaButtons: LeasesSummaryResponse['ctaButtons'];
  displayType?: string;
  isLoading?: boolean;
};

const ConnectedLeases = ({
  leaseCards = [],
  ctaButtons = [],
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  isLoading,
}: Props) => {
  const leaseCardsLength = leaseCards?.length || 0;
  const isMultipleLeases = leaseCardsLength > 1;
  const { user } = useSelector((state) => state);

  const handleCTAClick = (text: string) => {
    if (text === CREATE_A_LEASE) {
      ManageTenantsCSEvents.CreateALease({ user });
    }
  };

  const renderLeaseCards = (leases: LeasesSummaryResponse['leaseCards']) => {
    const leasesToRender = isLoading ? Array(2).fill({}) : leases;

    return leasesToRender.map((lease, i) => (
      <StyledFlexLeaseCard
        key={`leaseCard-${i}`}
        className={cx('LeaseCard', {
          'LeaseCard-Even': i % 2 === 0,
          'LeaseCard-Multiple': isMultipleLeases,
        })}
        mediaQuery={{
          xs: { flex: '0 50%' },
          md: { flex: isMultipleLeases ? '0 50%' : '1 50%' },
          lg: { flex: '0 50%' },
        }}
      >
        {/* @ts-expect-error */}
        <LeaseCard lease={lease} isMultipleLeases={isMultipleLeases} index={i} id={i} isLoading={isLoading} />
      </StyledFlexLeaseCard>
    ));
  };
  const renderCtaButtons = (buttons: LeasesSummaryResponse['ctaButtons']) => {
    if (isLoading) {
      return (
        <LoadingButton isLoading={true} loadingWidth="40%">
          Loading
        </LoadingButton>
      );
    }
    const styledButtons = buttons.map(({ text, url }, i) => (
      <Button
        data-test="connected-leases-cta-button"
        asChild
        tone="brand"
        emphasis="outlined"
        size={'md'}
        key={`connected-leases-cta-button-${i}`}
        onClick={() => handleCTAClick(text)}
        data-eventid={text === CREATE_A_LEASE ? ManageTenantsCSEvents.CreateALease.data_event_id : undefined}
      >
        <a href={url}>{text}</a>
      </Button>
    ));

    if (styledButtons.length === 2) {
      const [firstButton, secondButton] = styledButtons;
      return [firstButton, <Box key="connected-leases-cta-button-spacer" css={{ marginRight: '200' }} />, secondButton];
    }

    return styledButtons;
  };

  const renderFullType = () => {
    return (
      <>
        <ConnectedLeaseContainer display="flex" flexDirection="column" gap="sm" marginBottom="sm">
          {renderLeaseCards(leaseCards)}
        </ConnectedLeaseContainer>
        {renderCtaButtons(ctaButtons)}
      </>
    );
  };

  const renderSimpleType = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(1, minmax(0,1fr))',
            lg: leaseCardsLength < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
            xxl: leaseCardsLength < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
          },
          gap: { base: '200', lg: '400' },
        }}
      >
        {leaseCards.map((lease, i) => {
          return (
            <Box key={i}>
              {/* @ts-expect-error */}
              <LeaseCard lease={lease} isMultipleLeases={isMultipleLeases} index={i} id={i} displayType={displayType} />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <div data-testid="ConnectedLeases">
      {displayType === VERTICAL_DISPLAY_TYPE.SIMPLE ? renderSimpleType() : renderFullType()}
    </div>
  );
};

export default ConnectedLeases;
